import {
  createDefaultStandings,
  getTeamById,
  GROUP_LETTERS,
  type GroupLetter,
  type GroupStandings,
  type MatchId,
} from './bracket'

export type MatchSide = 'home' | 'away'
export type PredictionMode = 'current' | 'custom'
export type SavedPrediction = {
  v: 2
  orders: Partial<Record<GroupLetter, string[]>>
  thirds: GroupLetter[]
  mode: PredictionMode
  winners: Partial<Record<MatchId, MatchSide>>
}

export const PREDICTION_STORAGE_KEY = 'snapbracket:world-cup-2026-predictor'

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

export function serializeOrders(standings: GroupStandings) {
  return Object.fromEntries(
    GROUP_LETTERS.map((group) => [
      group,
      standings[group].map((team) => team.id),
    ]),
  ) as Record<GroupLetter, string[]>
}

export function restoreStandings(prediction: SavedPrediction): GroupStandings {
  const standings = createDefaultStandings()
  for (const group of GROUP_LETTERS) {
    const order = prediction.orders[group]
    if (order) standings[group] = order.map((id) => getTeamById(id)!)
  }
  return standings
}

export function encodePrediction(prediction: SavedPrediction) {
  return btoa(JSON.stringify(prediction))
}

export function decodePrediction(encoded: string): SavedPrediction | null {
  try {
    const parsed: unknown = JSON.parse(atob(encoded))
    if (!isRecord(parsed) || (parsed.v !== 1 && parsed.v !== 2)) return null
    if (!Array.isArray(parsed.thirds) || parsed.thirds.length > 8) return null
    if (
      parsed.thirds.some(
        (group) => !GROUP_LETTERS.includes(group as GroupLetter),
      ) ||
      new Set(parsed.thirds).size !== parsed.thirds.length
    ) {
      return null
    }

    const rawOrders = parsed.orders ?? {}
    if (!isRecord(rawOrders)) return null
    const orders: SavedPrediction['orders'] = {}
    for (const group of GROUP_LETTERS) {
      const ids = rawOrders[group]
      if (ids === undefined) {
        // 旧链接允许只携带部分小组；新存档必须保留完整排名。
        if (parsed.v === 2) return null
        continue
      }
      if (
        !Array.isArray(ids) ||
        ids.length !== 4 ||
        new Set(ids).size !== 4 ||
        ids.some(
          (id) => typeof id !== 'string' || getTeamById(id)?.group !== group,
        )
      ) {
        return null
      }
      orders[group] = ids
    }

    const winners: SavedPrediction['winners'] = {}
    if (parsed.v === 2) {
      if (parsed.mode !== 'current' && parsed.mode !== 'custom') return null
      if (!isRecord(parsed.winners)) return null
      for (const [id, side] of Object.entries(parsed.winners)) {
        // 本工具包含 M73–M102 和决赛 M104，不包含季军赛 M103。
        if (
          !/^M(7[3-9]|8[0-9]|9[0-9]|10[0-2]|104)$/.test(id) ||
          (side !== 'home' && side !== 'away')
        ) {
          return null
        }
        winners[id as MatchId] = side
      }
    }

    return {
      v: 2,
      orders,
      thirds: parsed.thirds as GroupLetter[],
      mode: parsed.v === 2 ? (parsed.mode as PredictionMode) : 'custom',
      winners: parsed.thirds.length === 8 ? winners : {},
    }
  } catch {
    return null
  }
}

export function loadPrediction(
  shared: string | null,
  getStorage: () => Pick<Storage, 'getItem'>,
): SavedPrediction | null {
  // 有效分享链接优先；无效链接回退到本地存档。
  const fromLink = shared ? decodePrediction(shared) : null
  if (fromLink) return fromLink
  try {
    const saved = getStorage().getItem(PREDICTION_STORAGE_KEY)
    return saved ? decodePrediction(saved) : null
  } catch {
    // 浏览器禁用存储时，仍可使用默认预测或分享链接。
    return null
  }
}

export function savePrediction(
  prediction: SavedPrediction,
  getStorage: () => Pick<Storage, 'setItem'>,
) {
  try {
    getStorage().setItem(PREDICTION_STORAGE_KEY, encodePrediction(prediction))
    return true
  } catch {
    // 存储不足或被禁用时，保留当前页面中的预测并由界面提示。
    return false
  }
}
